import { hiddenModeRefusal, isEmptyString } from "../validations";
import { readHidden, writeHidden } from "../visibility";
import type { CustomizationHandler } from "./types";

/**
 * The compare-at price on the product page (PDP) only: the struck-through
 * original price next to `product-detail-price`. Product grids get a target of
 * their own, `product-grid-item-compare-at-price`, for the same reason the
 * price does: every card has a different one.
 *
 * `.js-compare-price-display` alone also matches grid cards, quick shop, the
 * sticky "fixed" bars of atlantico and flex, and the payments modal of ipanema
 * and zenit-flex. Scoping it to `.js-price-container`, which only the product
 * page renders, leaves the one the shopper is looking at: in all 24 Core
 * themes with NubeSDK slots, and always as a plain-text element, so
 * `textContent` loses no markup. Same scope as `product-detail-price`.
 *
 * The theme rewrites this element on every variant change
 * (`.js-compare-price-display.text(variant.compare_at_price_short).show()`, or
 * `.hide()` when the variant has none), hence the app re-applying `text` on
 * `product:variant_selected`. `hidden` needs no re-applying: it is a class
 * with `!important`, which outranks the inline `display` that `.show()`
 * writes. See ../visibility.ts.
 *
 * @see ./README.md
 */
export const productDetailCompareAtPrice: CustomizationHandler<"product-detail-compare-at-price"> =
	{
		host: "storefront",
		selector: ".js-price-container .js-compare-price-display",

		read(element) {
			// The theme's own inline `display: none` (no compare-at price on this
			// variant) is deliberately not part of the snapshot: the SDK never
			// writes `display` here, so it has nothing of the theme's to give
			// back. See ../visibility.ts.
			return {
				text: element.textContent ?? "",
				hidden: readHidden(element),
			};
		},

		operation(element, options) {
			// `textContent` only: the theme computes the discount percentage from
			// the variant data, not from this text, which this leaves untouched.
			if (options.text !== undefined) element.textContent = options.text;
			if (options.hidden !== undefined) writeHidden(element, options.hidden);
		},

		validate(_element, options) {
			if (options.text !== undefined && isEmptyString(options.text)) {
				return 'refused "text": a price cannot be blank';
			}
			if (options.hidden !== undefined) {
				const refusal = hiddenModeRefusal(options.hidden);
				if (refusal) return `refused "hidden": ${refusal}`;
			}

			return null;
		},
	};
